import { Divider, FormControl, Grid, MenuItem, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import ContentCard from "../../components/ContentCard";
import { getCompanyId } from "../../utils/api";
import { apiRequest } from "../../utils/apiRequest";
import { buildChangeEventRequestBody } from "../../utils/requestBodies.js";
import { showToast } from "../../utils/toastOptions";
import ChangeEventLineItemsTable from "./ChangeEventLineItemsTable";
import SearchableSelect from "./SearchableSelect";
import { safeDisplay } from "./sharedDropdownOptions";

const inputStyle = {
  width: 300,
  "& .MuiInputBase-root": { height: 36 },
  "& .MuiInputBase-input": { padding: "0 12px" },
};
const selectStyle = { width: 300 };

// ---------- Numeric helpers (2-decimal, non-negative) ----------
const DEC2 = /^(\d+(\.\d{0,2})?)?$/; // allow empty, or digits with optional . and up to 2 decimals

const DEFAULT_ROW = {
  budgetCodeId: "",
  description: "",
  vendorId: "",
  contractId: "",
  unitOfMeasure: "",
  // Revenue (UI only)
  revenueQty: "",
  revenueUnitCost: "",
  revenueROM: null,
  // Cost (sent)
  costQty: "",
  costUnitCost: "",
  costROM: { method: "quantity_cost" }, // default calc matches qty * unit
  // Budget (UI only)
  budgetQty: "",
  budgetUnitCost: "",
  budgetROM: null,
};

const CreateChangeEventForm = forwardRef(
  (
    { formData, setFormData, changeEventDropdownOptions, selectedProject, setRefreshChangeEvents },
    ref
  ) => {
    const [rows, setRows] = useState([{ ...DEFAULT_ROW }]);
    const [localChangeEvent, setLocalChangeEvent] = useState(formData.changeEvent || {});

    useEffect(() => {
      setLocalChangeEvent(formData.changeEvent || {});
    }, [formData.changeEvent]);

    useImperativeHandle(ref, () => ({ handleSubmit }));

    const handleSubmit = async () => {
      if (!selectedProject?.id) return;

      const payload = buildChangeEventRequestBody(selectedProject.id, {
        ...formData,
        changeEvent: localChangeEvent,
        lineItems: rows,
      });

      try {
        const companyId = getCompanyId(selectedProject);
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${selectedProject.id}/change_events/`,
          { method: "POST", body: payload }
        );
        // Clear form
        setLocalChangeEvent({});
        setRows([{ ...DEFAULT_ROW }]);
        setRefreshChangeEvents((prev) => !prev);
        showToast("success", "Change Event created successfully!");
      } catch (e) {
        showToast("error", e.message || "Failed to create Change Event");
      }
    };

    const handleChange = (event) => {
      const { name, value } = event.target;
      const NUMERIC_ID_FIELDS = new Set([
        "status",
        "origin",
        "type",
        "primeContract",
        "changeReason",
      ]);
      let newValue = value;
      if (NUMERIC_ID_FIELDS.has(name)) {
        newValue = value === "" || value == null ? null : Number(value);
      }

      setLocalChangeEvent((prev) => ({ ...prev, [name]: newValue }));
    };

    const handleOriginChange = (e) => {
      const idVal = e.target.value === "" ? null : Number(e.target.value);
      setLocalChangeEvent((prev) => ({ ...prev, origin: idVal, originType: "rfi" }));
    };

    const handleRowChange = (index, field, value) => {
      setRows((prevRows) => {
        const updated = [...prevRows];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    };

    // Numeric change with 2-decimal validation
    const handleDec2Change = (index, field) => (e) => {
      const val = e.target.value;
      if (val === "" || DEC2.test(val)) {
        handleRowChange(index, field, val);
      }
    };

    const getDisplayText = (value, options, fieldName) => {
      if (value == null || value === "") return "";
      const option = options?.find((opt) => opt.id === value || opt.value === value);
      if (!option) return value;
      switch (fieldName) {
        case "budgetCode":
          return safeDisplay(
            option.code ? `${option.code} — ${option.name || ""}` : option.name || value
          );
        case "vendorId":
          return safeDisplay(option.company || option.name || value);
        case "contractId":
          return safeDisplay(option.title || option.name || value);
        case "unitOfMeasure":
          return safeDisplay(option.name || value);
        default:
          return safeDisplay(option.name || value);
      }
    };

    const rs = localChangeEvent.revenueSource; // top-level line_item_revenue_source
    const revenueInputsDisabled = rs !== "quantity_x_unit_cost"; // Procore derives revenue for other modes

    const handleROMChange = (rowIndex, romType, value) => {
      const updated = [...rows];
      updated[rowIndex] = { ...updated[rowIndex], [romType]: value };
      setRows(updated);
    };

    return (
      <ContentCard elevation={0} p={3}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Create New Change Event
        </Typography>

        <Typography variant="h6" gutterBottom>
          General Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Number
            </Typography>
            <TextField
              sx={inputStyle}
              id="number"
              name="number"
              autoComplete="off"
              value={localChangeEvent.number || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Title
            </Typography>
            <TextField
              sx={inputStyle}
              id="title"
              name="title"
              autoComplete="off"
              value={localChangeEvent.title || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Status
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="status"
                value={localChangeEvent.status ?? ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) =>
                  v === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select a Status</span>
                  ) : (
                    (changeEventDropdownOptions.status.find((i) => i.id === Number(v))?.name ?? v)
                  )
                }
              >
                <MenuItem value="" disabled>
                  <em>Select a Status</em>
                </MenuItem>
                {changeEventDropdownOptions.status.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Origin
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="origin"
                value={localChangeEvent.origin ?? ""}
                displayEmpty
                onChange={handleOriginChange}
                renderValue={(v) =>
                  v === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Origin</span>
                  ) : (
                    (changeEventDropdownOptions.origin.find((i) => i.id === Number(v))?.name ?? v)
                  )
                }
              >
                <MenuItem value="" disabled>
                  <em>Select Origin</em>
                </MenuItem>
                {changeEventDropdownOptions.origin.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Type
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="type"
                value={localChangeEvent.type ?? ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) => {
                  if (v === "") return <span style={{ color: "#9e9e9e" }}>Select Type</span>;
                  const opt = (changeEventDropdownOptions.type || []).find(
                    (t) => t.id === Number(v)
                  );
                  return opt?.name ?? v;
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select Type</em>
                </MenuItem>
                {(changeEventDropdownOptions.type || []).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Change Reason
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="changeReason"
                value={localChangeEvent.changeReason ?? ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) => {
                  if (v === "") return <span style={{ color: "#9e9e9e" }}>Select Reason</span>;
                  const opt = (changeEventDropdownOptions.changeReason || []).find(
                    (r) => r.id === Number(v)
                  );
                  return opt?.name ?? v;
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select Reason</em>
                </MenuItem>
                {(changeEventDropdownOptions.changeReason || []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Scope
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="scope"
                value={localChangeEvent.scope || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) =>
                  v === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Scope</span>
                  ) : (
                    (changeEventDropdownOptions.scope.find((i) => i.value === v)?.name ?? v)
                  )
                }
              >
                <MenuItem value="" disabled>
                  <em>Select Scope</em>
                </MenuItem>
                {changeEventDropdownOptions.scope.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Line Item Revenue Source
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="revenueSource"
                value={localChangeEvent.revenueSource || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) =>
                  v === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Source</span>
                  ) : (
                    (changeEventDropdownOptions.revenueSource.find((i) => i.value === v)?.name ?? v)
                  )
                }
              >
                <MenuItem value="" disabled>
                  <em>Select Source</em>
                </MenuItem>
                {changeEventDropdownOptions.revenueSource.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Prime Contract for Markup Estimates
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="primeContract"
                value={localChangeEvent.primeContract ?? ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(v) =>
                  v === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Prime Contract</span>
                  ) : (
                    (changeEventDropdownOptions.primeContract.find((i) => i.id === Number(v))
                      ?.title ?? v)
                  )
                }
              >
                <MenuItem value="" disabled>
                  <em>Select Prime Contract</em>
                </MenuItem>
                {changeEventDropdownOptions.primeContract.map((pc) => (
                  <MenuItem key={pc.id} value={pc.id}>
                    {pc.number ? `${pc.number} — ${pc.title}` : pc.title}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <TextField
              sx={{ width: 1265 }}
              id="description"
              name="description"
              rows={4}
              fullWidth
              multiline
              autoComplete="off"
              value={localChangeEvent.description || ""}
              onChange={(e) =>
                setLocalChangeEvent((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Line Items
        </Typography>
        <ChangeEventLineItemsTable
          rows={rows}
          onRowsChange={setRows}
          dropdownOptions={changeEventDropdownOptions}
          getDisplayText={getDisplayText}
          handleROMChange={handleROMChange}
          handleDec2Change={handleDec2Change}
          revenueInputsDisabled={revenueInputsDisabled}
        />
      </ContentCard>
    );
  }
);

CreateChangeEventForm.propTypes = {
  formData: PropTypes.shape({
    changeEvent: PropTypes.object,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  changeEventDropdownOptions: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.object),
    origin: PropTypes.arrayOf(PropTypes.object),
    type: PropTypes.arrayOf(PropTypes.object),
    changeReason: PropTypes.arrayOf(PropTypes.object),
    scope: PropTypes.arrayOf(PropTypes.object),
    revenueSource: PropTypes.arrayOf(PropTypes.object),
    primeContract: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  selectedProject: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    company: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setRefreshChangeEvents: PropTypes.func.isRequired,
};

export default CreateChangeEventForm;
